/** Components for `./dashboardPrefs`, split out so that module can stay a plain `.ts`. */
import * as React from "react";
import { notifyError } from "@authe/ui-core";
import { useApiClient } from "@authe/ui-core/api";
import { useOptionalTenant } from "@authe/ui-core/tenant";
import { createScopedStorage } from "@authe/fe-commons/storage";
import type {
  AttributionReadScope,
  AttributionResource,
  AttributionScope,
} from "@authe/fe-commons/attribution";
import { createApiSavedViewsSource, emptyView, type SavedView } from "@authe/saved-views";
import { useCurrentUserId } from "./storageIdentity";
import { APPLICATION_SLUG } from "@/config";
import type { DataOperation, Transport } from "@authe/fe-commons/transport";
import type { TxTenancyScopeId } from "@/features/transactions/scopes";
import {
  blobIsEmpty,
  CACHE_KEY,
  EMPTY_BLOB,
  EMPTY_CACHE,
  MANAGED_PREFS_LAYERS,
  PREFS_TABLE_ID,
  PREFS_VIEW_NAME,
  isKnownTxScope,
  managedScopeDefaults,
  // Aliased: `DashboardPrefs` has a FIELD of this name, and a local binding that
  // shadows a shared import is how the two silently swap places.
  managedTxFeed as resolveManagedTxFeed,
  normalizeBlob,
  normalizeHomeDashboardIds,
  resolveHomeDashboardIds,
  resolveWorkspaceGuide,
  normalizeCache,
  rebaseOwn,
  transportFallback,
  transportMasterFallback,
  transportPolicyFlags,
  withSection,
  type DashboardPrefsBlob,
  type DashboardPrefsCache,
  type DashboardPrefsSection,
  type ManagedPrefsLayer,
  type TransportPrefs,
} from "./dashboardPrefs.blob";
import { type DashboardPrefs, DashboardPrefsContext } from "./dashboardPrefs";

export function DashboardPrefsProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const tenant = useOptionalTenant();
  const client = useApiClient();

  const customerId = tenant?.customerId ?? null;
  const orgId = tenant?.orgId ?? null;
  const tenantId = tenant?.tenantId ?? null;
  const userId = tenant?.userId ?? null;
  const currentUserId = useCurrentUserId();

  // Identity-scoped keyspace: fresh on a tenant/user switch so a cached blob
  // never bleeds between identities (same pattern as brand overrides).
  const storage = React.useMemo(
    () => createScopedStorage({ app: APPLICATION_SLUG, customerId, orgId, tenantId, userId }),
    [customerId, orgId, tenantId, userId],
  );
  const source = React.useMemo(
    // NOT `userId` (the storage keyspace segment, derived from the tenant
    // claim): the source compares this against a row's `owner_user_id`, so it
    // must be the same value adapters uses. See `useCurrentUserId`.
    () => createApiSavedViewsSource(client, { currentUserId }),
    [client, currentUserId],
  );

  const [cache, setCache] = React.useState<DashboardPrefsCache>(() =>
    normalizeCache(storage.get<DashboardPrefsCache>(CACHE_KEY, EMPTY_CACHE)),
  );
  const [syncError, setSyncError] = React.useState<unknown>(null);

  // Mirror the latest layers for the setters (compose consecutive edits without
  // a side effect inside a state updater, which StrictMode double-invokes).
  const cacheRef = React.useRef(cache);
  // The server row id for THIS identity's `user` view; null until created/hydrated.
  const userViewId = React.useRef<string | null>(null);
  // Serialise server writes so a burst can't race a create into duplicate rows.
  const writeChain = React.useRef<Promise<void>>(Promise.resolve());
  // The latest `persist`, so the failure toast's Retry can re-push the very
  // layers that failed without `persist` having to close over itself.
  const persistRef = React.useRef<((next: DashboardPrefsCache) => void) | null>(null);
  // Whether THIS identity's rows have been read. Until they have, nothing here
  // knows which rows exist — and a write that guessed "none" created a second
  // one, which the next load then read instead of the first.
  const rowsKnown = React.useRef(false);
  // The read in flight (or the last one), for a write queued behind it.
  const rowsRead = React.useRef<Promise<void> | null>(null);
  // True while the last read failed: Retry then means "read again", not "write".
  const readFailed = React.useRef(false);
  // Bumped when the identity changes or the provider unmounts, so a read that
  // outlives its identity touches nothing.
  const generation = React.useRef(0);
  // Re-bases a personal write that was composed before the row was read.
  const rebase = React.useRef<((own: DashboardPrefsBlob) => DashboardPrefsBlob) | null>(null);

  const setBoth = React.useCallback((next: DashboardPrefsCache) => {
    cacheRef.current = next;
    setCache(next);
  }, []);

  // Seed from the local cache whenever the identity (keyspace) changes.
  React.useEffect(() => {
    userViewId.current = null;
    setBoth(normalizeCache(storage.get<DashboardPrefsCache>(CACHE_KEY, EMPTY_CACHE)));
  }, [storage, setBoth]);

  // The preference which enables server streaming must propagate even when
  // that transport is off. Reuse the scoped cache's cross-tab subscription for
  // every preference, without echoing the incoming value back to the server.
  React.useEffect(() => storage.subscribe(CACHE_KEY, () => {
    setBoth(normalizeCache(storage.get<DashboardPrefsCache>(CACHE_KEY, EMPTY_CACHE)));
  }), [storage, setBoth]);

  // Read this identity's rows, LAYER BY LAYER. Not a pick: the caller's own row
  // and the rows beneath it are all kept, and the shared resolver decides per
  // resource which one is in force. Rejects when the read fails.
  const readRows = React.useCallback((): Promise<void> => {
    const identity = generation.current;
    const startedWith = cacheRef.current;
    const read = (async () => {
      try {
        const views = await source.list({ tableId: PREFS_TABLE_ID });
        if (identity !== generation.current) return;
        const mine = views.find((v) => v.scope === "user");
        // A first edit may already have created the row while this list was
        // pending. An older empty response must not forget that row's id.
        userViewId.current ??= mine?.id ?? null;
        const managed: ManagedPrefsLayer[] = [];
        for (const layer of MANAGED_PREFS_LAYERS) {
          const row = views.find((v) => v.scope === layer.scope);
          if (!row) continue;
          managed.push({
            source: layer.source,
            viewId: row.id,
            blob: normalizeBlob(row.state?.extra),
          });
        }
        const serverOwn = mine ? normalizeBlob(mine.state?.extra) : EMPTY_BLOB;
        // A setting edited here or received from another tab while this read
        // was pending is newer than its response: never undo it. But only IT is
        // newer. Dropping the whole response for it, as this did, also dropped
        // the layers beneath — somebody else's rows, which no local edit can
        // have made stale — so one early toggle left the workspace's defaults
        // (its Quick start guide and pins) unread for the session, and the
        // admin's next publish created a second workspace row over the first.
        const edited = cacheRef.current !== startedWith;
        const startedOwn = startedWith.own;
        if (edited) rebase.current = (own) => rebaseOwn(serverOwn, startedOwn, own);
        const managedEdited =
          JSON.stringify(cacheRef.current.managed) !== JSON.stringify(startedWith.managed);
        // An ABSENT row is a real answer — "nobody has published this layer" —
        // so the hydrate replaces the cached layers wholesale rather than
        // merging into them. Merging would make a withdrawn workspace default
        // immortal on every machine that had ever seen it.
        const next: DashboardPrefsCache = {
          own: edited ? rebaseOwn(serverOwn, startedOwn, cacheRef.current.own) : serverOwn,
          managed: managedEdited ? cacheRef.current.managed : managed,
        };
        rowsKnown.current = true;
        if (readFailed.current) {
          readFailed.current = false;
          setSyncError(null);
        }
        setBoth(next);
        storage.set(CACHE_KEY, next);
      } catch (err) {
        // The local cache + defaults stand in, but the reader is told: their
        // saved defaults may simply not have been read, and the tiers they see
        // on screen are then not the ones their writes will use.
        if (identity === generation.current) {
          readFailed.current = true;
          setSyncError(err);
        }
        throw err;
      }
    })();
    rowsRead.current = read;
    return read;
  }, [source, storage, setBoth]);

  // Once per authenticated identity. Best-effort: a failure is reported
  // through `syncError`, and the next write (or Retry) reads again.
  React.useEffect(() => {
    if (!userId) return;
    rowsKnown.current = false;
    readFailed.current = false;
    rebase.current = null;
    void readRows().catch(() => undefined);
    return () => {
      generation.current += 1;
    };
  }, [readRows, userId]);

  /**
   * Resolve once this identity's rows are known, reading again after a read
   * that failed. Rejects when they cannot be read: a write that goes ahead
   * anyway can only guess that no row exists.
   */
  const knowRows = React.useCallback(async (): Promise<void> => {
    if (rowsKnown.current) return;
    await rowsRead.current?.catch(() => undefined);
    if (!rowsKnown.current) await readRows();
  }, [readRows]);

  /** Upsert one layer's row, returning the id (a create mints one). */
  const upsertRow = React.useCallback(
    async (
      scope: "user" | "tenant",
      blob: DashboardPrefsBlob,
      existingId: string | null,
    ): Promise<string> => {
      const state: SavedView["state"] = { extra: blob as unknown as Record<string, unknown> };
      if (existingId) {
        await source.update({
          id: existingId,
          name: PREFS_VIEW_NAME,
          tableId: PREFS_TABLE_ID,
          scope,
          state,
          createdAt: 0,
          updatedAt: 0,
        });
        return existingId;
      }
      const created = await source.create({
        ...emptyView(PREFS_TABLE_ID, scope),
        name: PREFS_VIEW_NAME,
        state,
      });
      return created.id;
    },
    [source],
  );

  // Write-through: cache immediately (drives UX + next first paint), then push
  // the caller's OWN row to the server on a serialised, best-effort chain.
  //
  // Only the `own` half is ever sent. The layers beneath it are somebody else's
  // row, and echoing them back under this caller's id is exactly the copy that
  // made a workspace default stick: once written as the user's own, it stopped
  // being inherited and stopped tracking the admin who set it.
  const persist = React.useCallback(
    (next: DashboardPrefsCache) => {
      storage.set(CACHE_KEY, next);
      if (!userId) return;
      // Queued before the rows were read: it waits for them, so it updates the
      // row that exists instead of creating a second, and it carries over what
      // that row says about the settings this edit did not touch.
      const beforeRead = !rowsKnown.current;
      writeChain.current = writeChain.current.then(async () => {
        try {
          setSyncError(null);
          let own = next.own;
          if (beforeRead) {
            await knowRows();
            own = rebase.current ? rebase.current(own) : own;
          }
          userViewId.current = await upsertRow("user", own, userViewId.current);
        } catch (err) {
          // The local cache already reflects the change, which is exactly why
          // this has to be said out loud: on screen the setting looks applied.
          setSyncError(err);
          notifyError(err, {
            title: "Preference not saved for your account",
            action: { label: "Retry", onClick: () => persistRef.current?.(next) },
          });
        }
      });
    },
    [knowRows, storage, upsertRow, userId],
  );

  persistRef.current = persist;

  /**
   * The retry behind the failure Home and Settings render: re-push the current
   * copy — or, when it was the READ that failed, read again. Pushing after a
   * failed read cleared the alert and left the guide, pins and defaults unread,
   * having written an empty row of the caller's own over whatever they had.
   */
  const retrySync = React.useCallback(() => {
    if (readFailed.current) {
      void readRows().catch(() => undefined);
      return;
    }
    persist(cacheRef.current);
  }, [persist, readRows]);

  const apply = React.useCallback(
    (own: DashboardPrefsBlob) => {
      const next: DashboardPrefsCache = { ...cacheRef.current, own };
      setBoth(next);
      persist(next);
    },
    [setBoth, persist],
  );

  /**
   * Publish (or withdraw) ONE SECTION of the workspace layer, then adopt the
   * result locally without a refetch.
   *
   * The published blob is the row as it stands with that section replaced —
   * never the caller's own blob whole. A publish used to overwrite `view_state`
   * with whatever the publishing admin's row said, so an admin who had
   * inherited a colleague's defaults (and therefore stated nothing about them)
   * withdrew them for everyone by saving an unrelated card.
   *
   * Awaited rather than fire-and-forget, unlike the personal write above: this
   * one changes what OTHER people see, and a publish that silently failed while
   * the admin watched the card say "saved" is a different order of wrong from a
   * personal preference that did not follow you to another browser.
   */
  const writeWorkspace = React.useCallback(
    async (section: DashboardPrefsSection, blob: DashboardPrefsBlob | null): Promise<void> => {
      // The workspace row must be known first: published without it, this
      // created a SECOND row holding one section, and everyone in the tenant
      // read that one from then on. Unreadable rows fail the publish.
      await knowRows();
      const existing = cacheRef.current.managed.find((layer) => layer.source === "workspace");
      const published = withSection(existing?.blob ?? EMPTY_BLOB, section, blob);
      const others = cacheRef.current.managed.filter((layer) => layer.source !== "workspace");

      // Nothing left in the row: delete it rather than store an empty one. The
      // hydrate reads an ABSENT row as "nobody has published this layer", and a
      // row that says nothing would report itself as a published default.
      if (blobIsEmpty(published)) {
        if (!existing?.viewId) return;
        await source.remove(existing.viewId);
        const next: DashboardPrefsCache = { ...cacheRef.current, managed: others };
        setBoth(next);
        storage.set(CACHE_KEY, next);
        return;
      }

      const viewId = await upsertRow("tenant", published, existing?.viewId ?? null);
      const layer: ManagedPrefsLayer = { source: "workspace", viewId, blob: published };
      const next: DashboardPrefsCache = { ...cacheRef.current, managed: [...others, layer] };
      setBoth(next);
      storage.set(CACHE_KEY, next);
    },
    [knowRows, setBoth, source, storage, upsertRow],
  );

  const setWriteScope = React.useCallback(
    (resource: AttributionResource, scope: AttributionScope | null) => {
      const own = cacheRef.current.own;
      const writeScope = { ...own.writeScope };
      // `null` DELETES the key rather than storing it: an absent entry is what
      // "inherit" is spelled as, and a stored null would be an override that
      // resolves to nothing and reports itself as a personal choice.
      if (scope == null) delete writeScope[resource];
      else writeScope[resource] = scope;
      apply({ ...own, writeScope });
    },
    [apply],
  );

  const setResolveScope = React.useCallback(
    (resource: AttributionResource, scope: AttributionReadScope | null) => {
      const own = cacheRef.current.own;
      const resolveScope = { ...own.resolveScope };
      if (scope == null) delete resolveScope[resource];
      else resolveScope[resource] = scope;
      apply({ ...own, resolveScope });
    },
    [apply],
  );

  const setTxFeed = React.useCallback(
    (scope: TxTenancyScopeId | null) => {
      apply({ ...cacheRef.current.own, txFeed: isKnownTxScope(scope) ? scope : null });
    },
    [apply],
  );

  const setHomeDashboardIds = React.useCallback((ids: string[] | null) => {
    const own = { ...cacheRef.current.own };
    if (ids === null) delete own.homeDashboardIds;
    else own.homeDashboardIds = normalizeHomeDashboardIds(ids);
    apply(own);
  }, [apply]);

  const setTransport = React.useCallback(
    (op: DataOperation, transport: Transport | null) => {
      const own = cacheRef.current.own;
      const operations = { ...own.transport.operations };
      // `null` DELETES the key: an absent entry is how "inherit" is spelled, and
      // a stored value equal to the inherited one is still an override — it stops
      // tracking the layer it was copied from the moment an admin changes it.
      if (transport == null) delete operations[op];
      else operations[op] = transport;
      apply({ ...own, transport: { ...own.transport, operations } });
    },
    [apply],
  );

  const setTransportMaster = React.useCallback(
    (enabled: boolean | null) => {
      const own = cacheRef.current.own;
      const transport: TransportPrefs = { ...own.transport };
      if (enabled == null) delete transport.master;
      else transport.master = enabled;
      apply({ ...own, transport });
    },
    [apply],
  );

  const setStreamingEnabled = React.useCallback((enabled: boolean | null) => {
    const own = cacheRef.current.own;
    const transport = { ...own.transport };
    if (enabled === null) delete transport.streaming;
    else transport.streaming = enabled;
    apply({ ...own, transport });
  }, [apply]);

  const publishWorkspaceDefaults = React.useCallback(
    (section: DashboardPrefsSection, blob: DashboardPrefsBlob) => writeWorkspace(section, blob),
    [writeWorkspace],
  );
  const clearWorkspaceDefaults = React.useCallback(
    (section: DashboardPrefsSection) => writeWorkspace(section, null),
    [writeWorkspace],
  );

  const value = React.useMemo<DashboardPrefs>(
    () => ({
      blob: cache.own,
      homeDashboardIds: resolveHomeDashboardIds(cache.own, cache.managed),
      workspaceGuide: resolveWorkspaceGuide(cache.own, cache.managed),
      setHomeDashboardIds,
      managed: managedScopeDefaults(cache.managed),
      managedTxFeed: resolveManagedTxFeed(cache.managed),
      workspaceDefaults:
        cache.managed.find((layer) => layer.source === "workspace")?.blob ?? null,
      setWriteScope,
      setResolveScope,
      setTxFeed,
      transportPolicy: transportPolicyFlags(cache.managed, cache.own.transport),
      transportFallback: (op: DataOperation) => transportFallback(cache.managed, op),
      transportMasterFallback: () => transportMasterFallback(cache.managed),
      setTransport,
      setTransportMaster,
      streamingEnabled: cache.own.transport.streaming ?? [...cache.managed].reverse()
        .find((layer) => typeof layer.blob.transport.streaming === "boolean")?.blob.transport.streaming ?? true,
      setStreamingEnabled,
      publishWorkspaceDefaults,
      clearWorkspaceDefaults,
      syncError,
      retrySync,
    }),
    [
      cache,
      setWriteScope,
      setResolveScope,
      setTxFeed,
      setHomeDashboardIds,
      setTransport,
      setTransportMaster,
      setStreamingEnabled,
      publishWorkspaceDefaults,
      clearWorkspaceDefaults,
      syncError,
      retrySync,
    ],
  );

  return <DashboardPrefsContext.Provider value={value}>{children}</DashboardPrefsContext.Provider>;
}
